import type { MetricResult } from '@/domain/metrics/contracts';
import { useFunnelMetrics } from './use-funnel-metrics';
import { formatCount, formatDecimal, formatMoneyMinorUnits, formatRatioAsPercent, formatUnknown } from './format';

function SummaryItem({ label, value, warn }: { label: string; value: string; warn?: boolean }) {
  return (
    <div className="flex min-w-[90px] flex-col gap-0.5">
      <span className="text-[10px] uppercase tracking-wide text-text-muted">{label}</span>
      <span className={`text-sm font-medium ${warn ? 'text-[#eaa52d]' : 'text-text-primary'}`}>{value}</span>
    </div>
  );
}

function displayMoney(result: MetricResult): string {
  return result.status === 'known' ? formatMoneyMinorUnits(result.effectiveValue as number | null) : formatUnknown();
}
function displayCount(result: MetricResult): string {
  return result.status === 'known' ? formatCount(result.effectiveValue as number | null) : formatUnknown();
}
function displayRatio(result: MetricResult): string {
  return result.status === 'known' ? formatRatioAsPercent(result.effectiveValue as number | null) : formatUnknown();
}
function displayDecimal(result: MetricResult): string {
  return result.status === 'known' ? `${formatDecimal(result.effectiveValue as number | null)}x` : formatUnknown();
}

/**
 * SPEC-008 §71/plan §26/T076 — a lightweight, always-derived funnel
 * summary. Deliberately not the DF-158 financial visualization (out of
 * scope): plain numbers only, no charts.
 */
export function FunnelMetricsSummary() {
  const metrics = useFunnelMetrics();
  if (!metrics) return null;
  const { funnel } = metrics;

  return (
    <div
      aria-label="Resumo de métricas do funil"
      className="flex flex-wrap items-center gap-4 border-b border-border-subtle bg-surface px-4 py-2"
    >
      <SummaryItem label="Investimento" value={displayMoney(funnel.investment)} />
      <SummaryItem label="Leads" value={displayCount(funnel.leads)} />
      <SummaryItem label="Clientes" value={displayCount(funnel.projectedClients)} />
      <SummaryItem label="Receita" value={displayMoney(funnel.revenue.status === 'known' ? funnel.revenue : funnel.projectedRevenue)} />
      <SummaryItem label="CPL" value={displayMoney(funnel.cpl)} />
      <SummaryItem label="CAC" value={displayMoney(funnel.cac)} warn={funnel.cac.warnings.length > 0} />
      <SummaryItem label="ROAS" value={displayDecimal(funnel.roas)} warn={funnel.roas.warnings.length > 0} />
      <SummaryItem label="ROI" value={displayRatio(funnel.roi)} />
    </div>
  );
}
